import type { TextInputProps } from '@_/ui';
import React from 'react';
import { PiInfo } from 'react-icons/pi';
import { useIntl } from 'react-intl';
import { LanguageCodes } from '@_/static';
import { TextInput } from '@_/ui';
import { resolvePromptMessage } from './resolvePromptMessage.tsx';

export type I18nTextInputValue = Record<string, string>;

export type I18nTextInputProps = Omit<TextInputProps, 'onBlur' | 'onChange' | 'value'> &
  (
    | {
        showAll: false;
        languageCode: string;
        onBlur?: () => void;
        onChange?: (value: I18nTextInputValue) => void;
        promptFillAllLanguages?: boolean;
        value: I18nTextInputValue;
      }
    | {
        showAll: true;
        onBlur?: () => void;
        onChange?: (value: I18nTextInputValue) => void;
        renderLabel: (languageCode: string) => React.ReactNode;
        value: I18nTextInputValue;
      }
  );

export function I18nTextInput({ onChange, value, ...props }: I18nTextInputProps) {
  const intl = useIntl();

  if (props.showAll) {
    const { autoFocus, renderLabel, showAll, ...rest } = props;

    return LanguageCodes.map((code, index) => (
      <TextInput
        key={code}
        {...rest}
        autoFocus={index === 0 ? autoFocus : undefined}
        label={renderLabel(code)}
        onChange={event =>
          onChange?.({
            ...value,
            [code]: event.target.value
          })
        }
        value={value[code] || ''}
      />
    ));
  }

  const { languageCode, promptFillAllLanguages = false, showAll, ...rest } = props;

  const promptMessage = promptFillAllLanguages
    ? resolvePromptMessage({
        languageCode,
        locale: intl.locale,
        value
      })
    : undefined;

  return (
    <TextInput
      {...rest}
      helpText={
        promptMessage ? (
          <span className="flex items-start gap-x-1">
            <span className="flex h-5 items-center">
              <PiInfo className="size-3.5" />
            </span>
            <span>{promptMessage}</span>
          </span>
        ) : undefined
      }
      onChange={event =>
        onChange?.({
          ...value,
          [languageCode]: event.target.value
        })
      }
      value={value[languageCode] || ''}
    />
  );
}
